import {
  addDays,
  endOfDay,
  isValid,
  isWithinInterval,
  startOfDay,
} from 'date-fns'
import { dateAdjustedForTimezone } from '@opallabs/lambda'
import { contentHasScheduledTime } from './momentTypes'
import type { PartialContent, PartialMoment } from './momentTypes'
import {
  formatHourId,
  formatHourLabel,
  NO_GROUPING_CATEGORY_ID,
} from './sortHelpers'
import {
  WeekViewCategory,
  WeekViewMode,
  WeekViewSort,
  WeekViewSortType,
} from './types'

type Channel = { system_name: string; name: string }
type LabelSet = {
  labels?: Array<{ name: string; position?: number }>
  label_sort_method?: string
}
type LabelSetMap = Record<string, LabelSet | undefined>

type BuildCategoriesArgs = {
  selectedSort: WeekViewSort
  viewMode: WeekViewMode
  content: Record<string, PartialContent | undefined> | null | undefined
  filteredContent: Record<string, PartialContent | undefined> | null | undefined
  moments: Record<string, PartialMoment | undefined> | null | undefined
  currentWeekChannels: Channel[] | null | undefined
  currentWeekLabelSets: LabelSetMap | null | undefined
  startDate: Date
  spanInMarketDays: boolean
}

const TIME_OF_DAY_CATEGORIES: WeekViewCategory[] = [
  { id: null, title: 'No Associated Time' },
  { id: 'morning', title: 'Morning' },
  { id: 'afternoon', title: 'Afternoon' },
  { id: 'evening', title: 'Evening' },
  { id: 'night', title: 'Night' },
]

const buildLabelSetCategories = (
  sort: WeekViewSort,
  isContent: boolean,
  contentItems: PartialContent[],
  moments: BuildCategoriesArgs['moments'],
  currentWeekLabelSets: LabelSetMap | null | undefined
): WeekViewCategory[] => {
  const noLabelCategory: WeekViewCategory = {
    id: null,
    title: `No Associated ${sort.title}`,
  }
  const labelSet = currentWeekLabelSets?.[sort.id]
  const baseLabels: WeekViewCategory[] = labelSet?.labels?.length
    ? labelSet.labels
        .map(({ name, position }) => ({
          id: name,
          title: name,
          position,
        }))
        .sort((a, b) =>
          labelSet.label_sort_method === 'manual'
            ? (a.position ?? 0) - (b.position ?? 0)
            : a.title.localeCompare(b.title)
        )
        .map(({ id, title }) => ({ id, title }))
    : []

  if (!isContent) {
    return baseLabels.length > 0
      ? [...baseLabels, noLabelCategory]
      : [noLabelCategory]
  }

  // Narrow to labels visible posts carry, with moment-label fallback (see
  // `contentGetters.getContentLabels`). Match by label_set id, not name —
  // two sets that share a display name shouldn't get conflated.
  const used = new Set<string>()
  let hasUncategorized = false
  const targetSetId = String(sort.id ?? '')
  const labelSetIdMatches = (label: { label_set?: { id?: string } }): boolean =>
    String(label?.label_set?.id ?? '') === targetSetId
  contentItems.forEach((c) => {
    const contentMatches = (c?.labels ?? []).filter(labelSetIdMatches)
    const momentId = c?.relationships?.moment?.id
    const parentMoment = momentId ? moments?.[momentId] : undefined
    const momentMatches =
      contentMatches.length === 0
        ? (parentMoment?.labels ?? []).filter(labelSetIdMatches)
        : []
    const matches = contentMatches.length > 0 ? contentMatches : momentMatches
    if (matches.length === 0) {
      hasUncategorized = true
      return
    }
    matches.forEach((l) => l?.name && used.add(l.name))
  })
  // Keep the catalog's labels (and their order) for labels in use, then append
  // any used label the catalog omits. The catalog only lists labels used by the
  // week's moments, so a post spanning in from an earlier week would otherwise
  // have its label row dropped and its span bar nowhere to render.
  const inCatalog = new Set(baseLabels.map((cat) => cat.id))
  const narrowed = [
    ...baseLabels.filter((cat) => cat.id != null && used.has(cat.id)),
    ...[...used]
      .filter((name) => !inCatalog.has(name))
      .sort((a, b) => a.localeCompare(b))
      .map<WeekViewCategory>((name) => ({ id: name, title: name })),
  ]
  return hasUncategorized || narrowed.length === 0
    ? [...narrowed, noLabelCategory]
    : narrowed
}

const buildChannelCategories = (
  isContent: boolean,
  contentItems: PartialContent[],
  currentWeekChannels: Channel[] | null | undefined
): WeekViewCategory[] => {
  const noChannelCategory: WeekViewCategory = {
    id: null,
    title: 'No Associated Channel',
  }
  const baseChannels: WeekViewCategory[] = currentWeekChannels
    ? currentWeekChannels
        .map(({ system_name, name }) => ({ id: system_name, title: name }))
        .sort((a, b) => a.title.localeCompare(b.title))
    : []

  if (!isContent) {
    return baseChannels.length > 0
      ? [...baseChannels, noChannelCategory]
      : [noChannelCategory]
  }

  // Content mode: derive rows from the content's own services rather than
  // narrowing the moment-derived `currentWeekChannels`. A post can span into a
  // week whose moments don't use its channel (its moment lives in an earlier
  // week), so `currentWeekChannels` omits that channel — narrowing against it
  // drops the row and the span bar has nowhere to render. Prefer the moment
  // list's display name when present, else the content's own service name.
  const titleBySystemName = new Map(baseChannels.map((c) => [c.id, c.title]))
  const titles = new Map<string, string>()
  let hasUncategorized = false
  contentItems.forEach((c) => {
    const systemName = c?.service?.system_name
    if (!systemName) {
      hasUncategorized = true
      return
    }
    if (!titles.has(systemName)) {
      titles.set(
        systemName,
        titleBySystemName.get(systemName) ?? c.service?.name ?? systemName
      )
    }
  })
  const categories = [...titles.entries()]
    .map<WeekViewCategory>(([id, title]) => ({ id, title }))
    .sort((a, b) => a.title.localeCompare(b.title))
  return hasUncategorized || categories.length === 0
    ? [...categories, noChannelCategory]
    : categories
}

const buildAccountCategories = (
  isContent: boolean,
  contentItems: PartialContent[],
  momentItems: PartialMoment[]
): WeekViewCategory[] => {
  // No dedicated wormhole selector for per-week accounts; derive from
  // whichever mode's source is authoritative. Content mode pulls from
  // `filteredContent` (post-filter); Moment mode from `moments` (also
  // post-filter). `account.name` is the category id, matching
  // `getContentAccount` / `getMomentAccount`.
  const noAccountCategory: WeekViewCategory = {
    id: null,
    title: 'No Associated Account',
  }
  const source = isContent ? contentItems : momentItems
  const used = new Set<string>()
  let hasUncategorized = false
  source.forEach((item) => {
    const name = item?.account?.name
    if (name) used.add(name)
    else hasUncategorized = true
  })
  const categories = [...used]
    .sort((a, b) => a.localeCompare(b))
    .map<WeekViewCategory>((name) => ({ id: name, title: name }))
  return hasUncategorized || categories.length === 0
    ? [...categories, noAccountCategory]
    : categories
}

/**
 * Whether a post is up, or in market, at some point in the week. Matches how
 * `contentMap` places it, so no row is built for a post it won't place.
 */
const overlapsWeek = (
  c: PartialContent,
  weekInterval: { start: Date; end: Date }
) => {
  if (typeof c.scheduled_at !== 'string') return true
  const start = dateAdjustedForTimezone(c.scheduled_at)
  if (!start || !isValid(start)) return true
  const end =
    (typeof c.takedown_scheduled_at === 'string' &&
      dateAdjustedForTimezone(c.takedown_scheduled_at)) ||
    start
  return !(end < weekInterval.start || start > weekInterval.end)
}

const buildContentTimeCategories = (
  contentItems: PartialContent[],
  startDate: Date,
  spanInMarketDays: boolean
): WeekViewCategory[] => {
  // Used hours only — empty hours don't surface. Span mode includes the hour
  // of every post in market during the week so cross-week range bars render.
  const noTimeCategory: WeekViewCategory = {
    id: null,
    title: 'Unscheduled',
  }
  const weekInterval = {
    start: startOfDay(startDate),
    end: endOfDay(addDays(startDate, 6)),
  }
  const usedHours = new Set<number>()
  let hasTimelessContent = false
  contentItems.forEach((c) => {
    if (typeof c?.scheduled_at !== 'string') return
    // Bucket by the same adjusted instant `contentMap`/`getContentTime` use to
    // place the card, so the hour rows the grid builds match where cards land
    // (a raw `new Date` drifts by a day at the week boundary for users whose
    // timezone differs from the workspace's, orphaning boundary cards).
    const scheduledAt = dateAdjustedForTimezone(c.scheduled_at)
    if (!scheduledAt || !isValid(scheduledAt)) return
    if (!spanInMarketDays && !isWithinInterval(scheduledAt, weekInterval))
      return
    // Timeless posts place in the null bucket (see `getContentTime`),
    // not their fallback midnight hour.
    if (!contentHasScheduledTime(c)) {
      hasTimelessContent = true
      return
    }
    usedHours.add(scheduledAt.getHours())
  })
  const categories = [...usedHours]
    .sort((a, b) => a - b)
    .map<WeekViewCategory>((hour) => ({
      id: formatHourId(hour),
      title: formatHourLabel(hour),
    }))
  // The "Unscheduled" row leads, matching the position of Moment mode's
  // "No Associated Time" row; it also doubles as the empty-state fallback.
  return hasTimelessContent || categories.length === 0
    ? [noTimeCategory, ...categories]
    : categories
}

/**
 * Builds the category rows for the WeekView grid based on the active sort.
 * Moment mode uses the full base list (channels / labels / time-of-day);
 * Content mode narrows to categories that actually have visible posts in
 * the week, keeping the grid free of empty rows.
 */
export const buildCategoriesForSort = ({
  selectedSort,
  viewMode,
  content,
  filteredContent,
  moments,
  currentWeekChannels,
  currentWeekLabelSets,
  startDate,
  spanInMarketDays,
}: BuildCategoriesArgs): WeekViewCategory[] => {
  const isContent = viewMode === WeekViewMode.Content
  // The store also holds content dated in other weeks, which Moments mode
  // loads for its cards, so rows come only from content in the week.
  const weekInterval = {
    start: startOfDay(startDate),
    end: endOfDay(addDays(startDate, 6)),
  }
  const contentItems = Object.values(filteredContent ?? content ?? {}).filter(
    (c): c is PartialContent => !!c && overlapsWeek(c, weekInterval)
  )
  const momentItems = Object.values(moments ?? {}).filter(
    (m): m is PartialMoment => Boolean(m)
  )

  switch (selectedSort.type) {
    case WeekViewSortType.LabelSet:
      return buildLabelSetCategories(
        selectedSort,
        isContent,
        contentItems,
        moments,
        currentWeekLabelSets
      )
    case WeekViewSortType.Channel:
      return buildChannelCategories(
        isContent,
        contentItems,
        currentWeekChannels
      )
    case WeekViewSortType.Account:
      return buildAccountCategories(isContent, contentItems, momentItems)
    case WeekViewSortType.None:
      return [{ id: NO_GROUPING_CATEGORY_ID, title: '' }]
    case WeekViewSortType.Time:
      return isContent
        ? buildContentTimeCategories(contentItems, startDate, spanInMarketDays)
        : TIME_OF_DAY_CATEGORIES
    default:
      return TIME_OF_DAY_CATEGORIES
  }
}
